import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Paper, Alert, InputAdornment, IconButton, CircularProgress } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { findUserByCredentials, updateUser } from './firebaseService';

const UserLogin = ({ onLogin, onBack }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Try Firebase first
      let user = null;
      try {
        user = await findUserByCredentials(username, password);
      } catch (firebaseError) {
        // Ignore Firebase errors for local testing
      }
      // If no user from Firebase, check localStorage
      if (!user) {
        const appUsers = JSON.parse(localStorage.getItem('appUsers') || '[]');
        user = appUsers.find(u => u.username === username && u.password === password && u.active);
      }
      if (user) {
        // Stamp lastLogin in Firebase (non-blocking; ignore errors so local-only logins still work)
        const loginAt = new Date().toISOString();
        if (user.id) {
          try {
            await updateUser(user.id, { ...user, lastLogin: loginAt });
            user = { ...user, lastLogin: loginAt };
          } catch (e) {
            // ignore — still allow login
          }
        }
        // Track successful login activity
        const activities = JSON.parse(localStorage.getItem('userActivities') || '[]');
        const newActivity = {
          id: Date.now(),
          username: user.username,
          action: 'Logged in',
          details: `User logged in successfully`,
          isError: false,
          timestamp: new Date().toISOString()
        };
        const updatedActivities = [newActivity, ...activities].slice(0, 100);
        localStorage.setItem('userActivities', JSON.stringify(updatedActivities));
        onLogin(user);
      } else {
        // Track failed login attempt
        const activities = JSON.parse(localStorage.getItem('userActivities') || '[]');
        const newActivity = {
          id: Date.now(),
          username: username || 'Unknown',
          action: 'Login failed',
          details: `Invalid credentials - Login attempt failed`,
          isError: true,
          timestamp: new Date().toISOString()
        };
        const updatedActivities = [newActivity, ...activities].slice(0, 100);
        localStorage.setItem('userActivities', JSON.stringify(updatedActivities));
        setError('Invalid credentials or inactive account. Please check your username and password.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Login error:', error);
      // Track error activity
      const activities = JSON.parse(localStorage.getItem('userActivities') || '[]');
      const newActivity = {
        id: Date.now(),
        username: username || 'Unknown',
        action: 'Connection error',
        details: `Error during login: ${error.message}`,
        isError: true,
        timestamp: new Date().toISOString()
      };
      const updatedActivities = [newActivity, ...activities].slice(0, 100);
      localStorage.setItem('userActivities', JSON.stringify(updatedActivities));
      setError('Connection error. Please check your internet connection and try again.');
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: { xs: 1.5, sm: 2 },
      position: 'relative'
    }}>
      {onBack && (
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{
            position: 'absolute',
            top: { xs: 10, sm: 20 },
            left: { xs: 10, sm: 20 },
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.1)'
            }
          }}
        >
          Back
        </Button>
      )}
      <Paper elevation={10} sx={{
        padding: { xs: 2.5, sm: 4 },
        maxWidth: 450,
        width: '100%',
        borderRadius: 3,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)'
      }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3 } }}>
          <CloudUploadIcon sx={{ fontSize: { xs: 48, sm: 60 }, color: '#667eea', mb: 1 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#333', mb: 1, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            CSV Upload Portal
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Sign in to access the application
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Username"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              setError('');
            }}
            required
            sx={{ mb: 2 }}
            variant="outlined"
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError('');
            }}
            required
            sx={{ mb: 2 }}
            variant="outlined"
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '12px',
              fontWeight: 600,
              fontSize: '1rem',
              mb: 2,
              position: 'relative',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              },
              '&:disabled': {
                background: '#ccc'
              }
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Sign In'}
          </Button>
        </form>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="caption" color="textSecondary">
            Don't have access? Contact your administrator.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default UserLogin;
